import React, { useState, useEffect } from 'react';
import './accountSettings.scss';
import profile from '../../assets/images/profile.svg';
import CryptoJS from 'crypto-js';
import ApiServices from '../../services/apiServices';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import { useNavigate } from "react-router-dom";

const AccountSettings = () => {
  const [userInfo, setUserInfo] = useState({});
  const [userPlan, setUserPlan] = useState({});
  const [displayName, setDisplayName] = useState('');
  const [memberShip, setMemberShip] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [statusType, setStatusType] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (localStorage.getItem('userKey')) {
      try {
        const bytes = CryptoJS.AES.decrypt(
          localStorage.getItem('userKey'),
          process.env.REACT_APP_SECERET_KEY
        );
        const parsedUser = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
        setUserInfo(parsedUser);
        setDisplayName(parsedUser.displayName || '');
        checkMembership();
      } catch (err) {
        console.error('Invalid user data');
      }
    } else {
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  const checkMembership = async () => {
    try {
      const result = await ApiServices.callGetAPI('membership/validate');
      if (result.status === 200 && result.data.valid) {
        setMemberShip(Number(result?.data?.data?.premiumuser || 0) > 0);
        setUserPlan(result.data.data);
      } else {
        setMemberShip(false);
      }
    } catch (error) {
      console.log(error);
      setMemberShip(false);
    }
  };

  const handleDeleteAccount = () => {
    navigate('/delete-account');
  };

  const handleSubscribe = () => {
    navigate('/subscricption');
  };

  const handleSaveProfile = async () => {
    const name = (displayName || '').trim();
    if (!name) {
      setStatusType('error');
      setStatusMsg('Display name cannot be empty.');
      return;
    }
    setSavingName(true);
    setStatusMsg('');
    try {
      const currentUser = firebase.auth().currentUser;
      if (!currentUser) {
        throw new Error('Session expired');
      }
      await currentUser.updateProfile({ displayName: name });
      const updatedUser = { ...userInfo, displayName: name };
      setUserInfo(updatedUser);
      const encrypted = CryptoJS.AES.encrypt(
        JSON.stringify(updatedUser),
        process.env.REACT_APP_SECERET_KEY
      ).toString();
      localStorage.setItem('userKey', encrypted);
      await ApiServices.callPostAPI('user/sync', {
        name,
        email: updatedUser.email || currentUser.email || '',
        contact: updatedUser.phoneNumber || currentUser.phoneNumber || '',
        profilePicture: updatedUser.photoURL || currentUser.photoURL || '',
        datafield: {
          displayName: name,
          email: updatedUser.email || currentUser.email || '',
          phoneNumber: updatedUser.phoneNumber || currentUser.phoneNumber || '',
          profilePicture: updatedUser.photoURL || currentUser.photoURL || '',
        },
      });
      setStatusType('success');
      setStatusMsg('Profile updated successfully.');
    } catch (error) {
      setStatusType('error');
      setStatusMsg('Unable to update profile right now.');
    } finally {
      setSavingName(false);
    }
  };

  const handleResetPassword = async () => {
    try {
      const email = userInfo.email || firebase.auth().currentUser?.email;
      if (!email) {
        setStatusType('error');
        setStatusMsg('No email found for this account.');
        return;
      }
      await firebase.auth().sendPasswordResetEmail(email);
      setStatusType('success');
      setStatusMsg('Password reset link sent to your email.');
    } catch (error) {
      setStatusType('error');
      setStatusMsg('Could not send reset email.');
    }
  };

  const logout = async () => {
    try {
      await firebase.auth().signOut();
      localStorage.clear();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <div className="acc-body">
      <div className="acc-body-inner card">
        <h1>Account & Settings</h1>

        <div className="profile-section">
          <img
            src={userInfo.photoURL || profile}
            className="profile-pic"
            alt="profile pic"
            onError={({ currentTarget }) => {
              currentTarget.onerror = null;
              currentTarget.src = profile;
            }}
          />
          <h2>{userInfo.displayName || 'User'}</h2>
          <p>{userInfo.email || 'No Email Available'}</p>
        </div>

        <div className="plan-section">
          <p className="heading">Profile Details</p>
          <label className="field-label">Display Name</label>
          <input
            type="text"
            className="profile-input"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Enter your display name"
          />
          <label className="field-label">Email</label>
          <input
            type="text"
            className="profile-input"
            value={userInfo.email || ''}
            readOnly
          />
          <button className="button save-button" onClick={handleSaveProfile} disabled={savingName}>
            {savingName ? 'Saving...' : 'Save Profile'}
          </button>
        </div>

        <div className="plan-section">
          <p className="heading">Membership</p>
          {memberShip && userPlan ? (
            <>
              <p>
                Current Plan: <span className="plan-details">{userPlan.membershiptype || 'Premium'}</span>
              </p>
              <p>
                Start Date: <span className="plan-details">{userPlan.starton || '-'}</span>
              </p>
              <p>
                End Date: <span className="plan-details">{userPlan.endon || '-'}</span>
              </p>
              <p>
                Days Left: <span className="plan-details">{userPlan.opendays || 0}</span>
              </p>
            </>
          ) : (
            <>
              <p className="muted-text">No active membership found.</p>
              <button className="button subscribe-button" onClick={handleSubscribe}>
                Subscribe
              </button>
            </>
          )}
        </div>

        <div className="plan-section">
          <p className="heading">Security</p>
          <button className="button secondary-button" onClick={handleResetPassword}>
            Send Password Reset Email
          </button>
        </div>

        {statusMsg && <p className={`status-message ${statusType === 'error' ? 'error' : 'success'}`}>{statusMsg}</p>}

        <button className="button logout-button" onClick={logout}>
          Logout
        </button>

        <button className="button delete-button" onClick={handleDeleteAccount}>
          Delete Account
        </button>
      </div>
    </div>
  );
};

export default AccountSettings;
